import { Component, Input, Output, EventEmitter, inject, OnInit, OnChanges, SimpleChanges, AfterViewInit, signal } from '@angular/core';
import { FormGroup, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { SelectOptions } from '../../../../../core/interfaces/select-options.interface';
import { HttpStatusCode } from 'axios';
import { NewRepApplicationService } from '../../new-rep-application-services/new-rep-application.service';
import { MatButtonModule } from '@angular/material/button';
import { ViewNewRepApplication } from '../../new-rep-application-interfaces/viewnew-rep-application.interface';
import { viewAttachements } from '../../new-rep-application-interfaces/attachements.interface';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmPopupComponent } from '../../confirm-popup/confirm-popup.component';
import { SnackbarService } from '../../../../../shared/services/snackbar/snackbar.service';
import { noWhitespace } from '../../../../../shared/validators/no-whitespace.validator';
@Component({
  selector: 'app-bank-details',
  imports: [
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    MatIcon,
    MatSelectModule,
    MatButtonModule
  ],
  templateUrl: './bank-details.component.html',
  styleUrl: './bank-details.component.scss'
})
export class BankDetailsComponent implements OnInit {
  @Output() next = new EventEmitter<string[]>();
  @Input() formGroup!: FormGroup;
  @Input() bankdetails!: ViewNewRepApplication;
  @Input() viewattachments!: viewAttachements[];
  @Input() readonly = signal(false);
  @Output() preview = new EventEmitter<string[]>();

  private fb = inject(FormBuilder);
  private readonly matDialog = inject(MatDialog);
  private newRepApplicationService = inject(NewRepApplicationService);
  private snackBarService = inject(SnackbarService);
  status = 'Not Started';
  readonly message = 'This field is required.';
  protected banknames!: SelectOptions[];

  async ngOnInit() {

    this.formGroup.addControl('bankName', this.fb.control('', [Validators.required]));
    this.formGroup.addControl('branchname', this.fb.control('', [Validators.required,noWhitespace]));
    this.formGroup.addControl('accountNumber', this.fb.control('', [Validators.required,noWhitespace]));
    this.formGroup.addControl('attachmentbankaccoutndetails', this.fb.control([null]));
    this.formGroup.addControl('attachmentbankaccoutndetailsfileName', this.fb.control('', [Validators.required]));

    this.getBankDetailsDropdownOptions();
    await this.fetchBankDetails();
    this.handleFormStatus();
  }

  fetchBankDetails(): void {
    this.formGroup.controls['bankName']?.setValue(this.bankdetails?.bankname);
    if (this.bankdetails?.bankBranchName != 'PENDING') {
      this.formGroup.controls['branchname']?.setValue(this.bankdetails?.bankBranchName);
    }
    if (this.bankdetails?.bankAccountNumber != '0000000000') {
      this.formGroup.controls['accountNumber']?.setValue(this.bankdetails?.bankAccountNumber);
    }
    this.viewattachments.forEach(item => {
      if (item?.onboardingDocumentType === 'Attachement(Bank Account)') {
        this.formGroup.controls['attachmentbankaccoutndetailsfileName']?.setValue(item?.fileName);
      }
    })
    if (this.formGroup.valid) {
      this.status = 'Completed';
      this.next.emit([this.status, 'submitted']);
    } else if (this.formGroup.invalid) {
      const anyFilled = Object.values(this.formGroup.value).some(v => v && v.toString().trim() !== '');
      if (anyFilled) {
        this.status = 'In Progress';
        this.next.emit([this.status, 'submitted']);
      } else {
        this.status = 'Not Started';
        this.next.emit([this.status, 'submitted']);
      }
    }
    if (this.readonly() == true) {
      this.formGroup?.disable();
    } else {
      this.formGroup?.enable();
    }
  }

  handleFormStatus() {
    if (this.readonly()) return;

    const initialValue = this.formGroup.getRawValue();

    this.formGroup.valueChanges.subscribe(currentValue => {
      const hasChanged =
        JSON.stringify(initialValue) !== JSON.stringify(currentValue);

      if (hasChanged) {
        this.status = 'In Progress';
        this.next.emit([this.status, 'change']);
      }
    });
  }

  onAttachmentbankaccoutndetailsFileChange(event: any): void {
    const file = event.target.files?.[0];
    const fileControl = this.formGroup.get('attachmentbankaccoutndetailsfileName');
    if (file) {
      this.formGroup.patchValue({
        attachmentbankaccoutndetails: file,
        attachmentbankaccoutndetailsfileName: file.name
      });
    }
    this.formGroup.controls['attachmentbankaccoutndetailsfileName'].markAsTouched();
    this.formGroup.controls['attachmentbankaccoutndetailsfileName'].updateValueAndValidity();
    if (!file) {
      fileControl?.setValue(null);
      return;
    }

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    // ❌ Invalid type
    if (!allowedTypes.includes(file.type)) {
      fileControl?.setErrors({ invalidType: true });
      return;
    }

    // ❌ Invalid size
    if (file.size > maxSize) {
      fileControl?.setErrors({ maxSize: true });
      return;
    }

    // ✅ Valid file
    fileControl?.setErrors(null);
    // fileControl?.setValue(file);
    this.uploaddocument();
  }

  async uploaddocument() {
    const formData = new FormData();

    formData.append('file', this.formGroup.get('attachmentbankaccoutndetails')?.value);
    formData.append('Description', 'Attachement(Bank Account)');
    formData.append('filename', this.formGroup.get('attachmentbankaccoutndetails')?.value?.name);
    formData.append('ResponseValue', '');

    const responseData = await this.newRepApplicationService
      .uploadDocuments(formData)
      .then((response) => {
        return JSON.parse(response);
      })
      .catch((error) => {
        // console.error(error);
        return error;
      });
    if (responseData.status === HttpStatusCode.Ok) {
      this.formGroup.patchValue({
        attachmentbankaccoutndetailsfileName: responseData.data.responsedata
      });
      this.viewDocumants();
      this.snackBarService.openSuccessSnackBar("document uploaded successfully!");
    } else {
      if (responseData.code === HttpStatusCode.BadRequest) {
        // Display notification on API failure.
        this.snackBarService.openFailureSnackBar("We couldn’t process your request. Please try again");
      }
    }
  }

  removeFile(controlName: string, fileVar: string, categoryname: string) {
    const dialogRef = this.matDialog.open(ConfirmPopupComponent, {
      width: '500px',
      disableClose: true,
      data: {
        message: 'Do you want to remove the attachement?',
        hidecancel: false,
      }
    });
    this.viewDocumants();
    dialogRef.afterClosed().subscribe(async result => {
      if (result.data === true) {
        if (this.viewattachments) {
          let onboardingId;
          let id;
          let docTypeId;
          this.viewattachments.forEach(item => {
            if (item?.onboardingDocumentType === categoryname) {
              onboardingId = item.onboardingId;
              docTypeId = item.docTypeId;
              id = item.id
            }
          })

          const data = {
            onboardingId: onboardingId,
            docTypeId: docTypeId,
            id: id
          }
          const responseData = await this.newRepApplicationService
            .deletedocument(data)
            .then((response) => {
              return JSON.parse(response);
            })
            .catch((error) => {
              // console.error(error);
              return error;
            });
        }
        this.formGroup.get(controlName)?.reset();
        this.formGroup.get(fileVar)?.reset();
        const fileInput = document.getElementById(controlName) as HTMLInputElement;
        if (fileInput) {
          fileInput.value = '';
        }
        this.formGroup.controls[controlName].markAsTouched();
        this.formGroup.controls[controlName].updateValueAndValidity();
      } else {
        // console.log('User cancelled');
      }
    });
  }

  async viewDocumants() {
    const responseData = await this.newRepApplicationService
      .viewApplicationDetails(this.bankdetails?.id)
      .then((response) => {
        return JSON.parse(response);
      })
      .catch((error) => {
        // console.error(error);
        return error;
      });

    this.viewattachments = responseData.data.responsedata.attachments;
  }

  async getBankDetailsDropdownOptions() {

    const responseData = await this.newRepApplicationService
      .getDropdownOptions()
      .then((response) => {
        return JSON.parse(response);
      })
      .catch((error) => {
        // console.error(error);
        return error;
      });


    if (responseData.status === HttpStatusCode.Ok) {
      this.banknames = responseData.data.responsedata.constants.bank_list;
    }
  }


  saveAndContinue() {

    // Save API call or form validation here
    if (this.formGroup.valid) {
      this.status = 'Completed';

    }
    this.formGroup.markAllAsTouched();
    this.next.emit([this.status, 'submit']);
  }

  previewAttachment(controlName: string, categoryName: string) {
    if (this.viewattachments) {
      let id;
      this.viewattachments.forEach(item => {
        if (item?.onboardingDocumentType === categoryName) {
          id = item.id;
        }
      });
      let filename = this.formGroup.get(controlName)?.value;
      this.preview.emit([id, filename]);
    }
  }

}
